<script setup lang="ts">
import WidgetPanels from "@/components/widget/WidgetPanels.vue";
import CommonFilter from "@/components/common/CommonFilter.vue";
import CustomChildList from "@/components/custom/CustomChildList.vue";
import {useChatStore} from "@/features/chat/chat.store";
import {onMounted, ref, watch} from "vue";
import {IChat} from "@/features/chat/chat.types";
import WidgetChatPanel from "@/components/widget/WidgetChatPanel.vue";
import {usePersonalStore} from "@/features/personal/personal.store";
import {useRoute} from "vue-router";
import {SearchMd} from "utruckin-icons";
import CustomSearch from "@/components/custom/CustomSearch.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import {IDropdown} from "@/types/main";
import {useAccountStore} from "@/features/account/account.store";
import {IAccount} from "@/features/account/account.types";
import CommonDatepicker from "@/components/common/CommonDatepicker.vue";
import {useShiftStore} from "@/features/shifts/shifts.store";
import {IShift} from "@/features/shifts/shifts.types";
import CustomTab from "@/components/custom/CustomTab.vue";

const route = useRoute();
const chatStore = useChatStore();
const personalStore = usePersonalStore();
const accountStore = useAccountStore()
const shiftStore = useShiftStore()

const chatLoading = ref<boolean>(false);
const chatList = ref<IChat>();
const chatMessageLoading = ref<boolean>(false);
const chatMessages = ref<any>();
const chatTopPageNumber = ref<number>(-1);
const chatBottomPageNumber = ref<number>(-1);
// accounts
const accounts = ref<IDropdown[]>([])
const accountLoading = ref<boolean>(false);
const accountType = ref(0);
// shifts
const shiftLoading = ref<boolean>(false);
const shifts = ref<IDropdown[]>([])
const shiftType = ref(0);
// outgoing
const activeTab = ref<number>(0);
const tabs = [
  {label: "All", value: 0},
  {label: "Sent", value: 1},
  {label: "Received", value: 2},
]

const getChats = async () => {
  chatTopPageNumber.value = route.query.prev_page;
  chatBottomPageNumber.value = route.query.next_page;

  if (route.query.page) {
    chatStore.commonFilter.page = route.query.page;
  }

  if (route.query.page_size) {
    chatStore.commonFilter.page_size = route.query.page_size;
  }

  chatList.value = await chatStore.getChats(chatLoading);
}

const fetchMoreChats = async (type: 'top' | 'bottom') => {
  if (type === 'top') {
    if (chatTopPageNumber.value) {
      chatStore.commonFilter.page = chatTopPageNumber.value;

      const newTopData: IChat = await chatStore.getChats(chatLoading);
      if (newTopData && chatList.value) {
        chatList.value.items = [...newTopData.items, ...chatList.value?.items];
        chatTopPageNumber.value = newTopData.prev_page;
      }
    }
  } else {
    if (chatBottomPageNumber.value) {
      chatStore.commonFilter.page = chatBottomPageNumber.value;

      const newBottomData: IChat = await chatStore.getChats(chatLoading);
      if (newBottomData && chatList.value) {
        chatList.value.items = [...chatList.value?.items, ...newBottomData.items];
        chatBottomPageNumber.value = newBottomData.next_page;
      }
    }
  }
}

const getChatMessages = async () => {
  await personalStore.getPersonalChats(chatMessageLoading, route.params.id, route.query.chat, (res: any) => {
    chatMessages.value = res.data.items;
  });
}

const getAccounts = async () => {
  accounts.value = []

  accountStore.accountsForFilter = await accountStore.getAccounts(accountLoading)

  if (accountStore.accountsForFilter.length) {
    accountStore.accountsForFilter.forEach((item: IAccount) => {
      accounts.value.push({name: item.session_name, value: item.id});
    })

    accounts.value.unshift({name: "All accounts", value: 0});
  }
}

const getShifts = async () => {
  shifts.value = []

  shiftStore.shiftsForFilter = await shiftStore.getShifts(shiftLoading, true)

  if (shiftStore.shiftsForFilter.length) {
    shiftStore.shiftsForFilter.forEach((item: IShift) => {
      shifts.value.push({name: item.title, value: item.id});
    })

    shifts.value.unshift({name: "All shift", value: 0});
  }
}

onMounted(() => {
  getChats();
  getChatMessages();
  getAccounts();
  getShifts()
})

watch(
    () => route.fullPath,
    () => {
      getChatMessages()
    }
)
</script>
<template>
  <div class="v-chat-child">
    <div class="main">
      <div class="main__body">
        <div class="main__body--list">
          <div class="header">
            <div class="search">
              <CustomSearch width="100%" :icon="SearchMd" placeholder="Users"/>
            </div>
            <div class="header__account">
              <CommonDropdown
                  width="100%"
                  label="Accounts"
                  v-model="accountType"
                  :items="accounts"
                  :disabled="!accounts.length || accountLoading "
              />
            </div>
          </div>
          <template v-if="chatList && chatList.items && chatList.items.length">
            <CustomChildList :list="chatList.items" @scroll="fetchMoreChats" :loading="chatLoading"/>
          </template>
        </div>
        <div class="main__body--chat">
          <div class="header">
            <div class="date">
              <CommonDatepicker
                  mode="single"
                  :default-range="{
            fromDate: new Date(yesterday),
            toDate: new Date(today),
          }"
                  :is-two-sinded="true"
                  placement="right"
                  :clearable="true"
              />
            </div>
            <div class="shift">
              <CommonDropdown
                  v-model="shiftType"
                  label="All shift"
                  :items="shifts"
                  :disabled="!shifts.length || shiftLoading"
              />
            </div>
            <div class="outgoing">

              <CustomTab :tabs="tabs" v-model="activeTab"/>
            </div>
          </div>
          <template v-if="chatMessages && chatMessages.length">
            <WidgetChatPanel :messages="chatMessages"/>
          </template>
        </div>
      </div>
    </div>
    <div class="aside">
      <WidgetPanels position="vertical"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.v-chat-child {
  display: flex;


  .main {
    width: 80%;

    &__body {
      display: flex;

      &--list, &--chat {
        .header {
          padding: 16px;
          border-bottom: 1px solid var(--color-border);
          display: flex;
          gap: 8px;
        }
      }

      &--list {
        width: 30%;
        border-right: 1px solid var(--color-border);
      }

      &--chat {
        width: 70%;
      }
    }
  }

  .aside {
    height: calc(100vh - 72px);
    width: 20%;
    overflow: auto;
    border-left: 1px solid var(--color-border);
    padding: 16px;
  }
}
</style>